/**
 * Interpretación de la opinión de cumplimiento del SAT.
 *
 * El dato que le importa al contribuyente —positivo o negativo— está impreso en
 * el PDF. Aquí se lee para no obligarlo a abrir el documento y buscarlo entre
 * sellos, cadenas originales y artículos del CFF.
 *
 * REGLA QUE MANDA SOBRE TODAS: ante cualquier duda, `indeterminado`. Un
 * veredicto de cumplimiento fiscal equivocado es peor que no dar veredicto, así
 * que nada se deduce por ausencia y nada se elige cuando las fuentes discrepan.
 */

import { extraerTexto, normalizar, antiguedad as antiguedadCompartida } from './pdfTexto';

export const POSITIVO = 'positivo';
export const NEGATIVO = 'negativo';
export const INDETERMINADO = 'indeterminado';

/**
 * Reexportada para no romper a quien ya la importaba de aquí. Vive en
 * `pdfTexto` porque la constancia la necesita igual.
 */
export const antiguedad = antiguedadCompartida;

/**
 * Cadena original del documento: `||RFC|FOLIO|DD-MM-AAAA|P||...`
 *
 * Es la mejor fuente de los datos secundarios: una sola línea, separadores
 * fijos, y no la afecta el maquetado de la hoja.
 */
const leerCadenaOriginal = (texto) => {
  const m = texto.match(/\|\|\s*([^|]{5,20})\|([^|]{1,40})\|(\d{2}-\d{2}-\d{4})\|([PN])\|\|/);
  if (!m) return null;

  return {
    rfc: m[1].trim(),
    folio: m[2].trim(),
    fecha: m[3],
    sentido: m[4] === 'P' ? POSITIVO : NEGATIVO,
  };
};

/**
 * El sentido, buscado como palabra completa en TODO el documento.
 *
 * No se busca junto a la etiqueta "Sentido" a propósito: el PDF lo genera
 * Apache FOP, que emite la fila de etiquetas y luego la fila de valores, así
 * que lo que sigue a "Sentido" en el orden de lectura es el nombre del
 * contribuyente. Un `/Sentido\s+(\w+)/` devolvería "ADELAIDA".
 *
 * Si aparecen los dos tokens, el documento no está diciendo una sola cosa: no
 * se elige ninguno.
 */
const leerToken = (texto) => {
  const positivo = /\bPOSITIVO\b/.test(texto);
  const negativo = /\bNEGATIVO\b/.test(texto);

  if (positivo === negativo) return null; // ninguno, o ambos
  return positivo ? POSITIVO : NEGATIVO;
};

/** `DD-MM-AAAA` → Date, o null. Sin depender de parsear meses en español. */
const aFecha = (ddmmaaaa) => {
  if (!ddmmaaaa) return null;
  const [d, m, a] = ddmmaaaa.split('-').map(Number);
  const fecha = new Date(a, m - 1, d);
  return Number.isNaN(fecha.getTime()) ? null : fecha;
};

const PATRON_RFC = /\b([A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3})\b/;

/**
 * Interpreta el TEXTO de una opinión. Puro y sin dependencias: aquí viven los
 * casos difíciles y es lo que se puede probar sin montar pdf.js ni React.
 *
 * @param {string} textoCrudo texto extraído del PDF (capa nativa u OCR)
 * @returns {{sentido: string, rfc: ?string, folio: ?string, emitidaEn: ?Date, motivo: ?string}}
 */
export const interpretarTexto = (textoCrudo) => {
  const texto = normalizar(textoCrudo);

  if (texto.length < 20) {
    return { sentido: INDETERMINADO, rfc: null, folio: null, emitidaEn: null, motivo: 'sin_texto' };
  }

  const cadena = leerCadenaOriginal(texto);
  const token = leerToken(texto);

  let sentido = INDETERMINADO;
  let motivo = null;

  if (token === null) {
    // Sin token no se infiere nada, ni siquiera con la cadena original: la
    // ausencia de "NEGATIVO" no significa positivo.
    motivo = 'sin_sentido';
  } else if (cadena && cadena.sentido !== token) {
    // Dos fuentes del mismo documento que no dicen lo mismo. Elegir una sería
    // apostar sobre el dato que menos admite apuestas.
    motivo = 'discrepancia';
  } else {
    sentido = token;
  }

  const rfc = cadena?.rfc || texto.match(PATRON_RFC)?.[1] || null;

  return {
    sentido,
    rfc,
    folio: cadena?.folio || null,
    emitidaEn: aFecha(cadena?.fecha),
    motivo,
  };
};

/**
 * Lee una opinión de cumplimiento y devuelve su interpretación.
 *
 * @param {Blob} blob PDF descargado
 * @returns {Promise<{sentido, rfc, folio, emitidaEn, motivo, origen}>}
 */
export const interpretarOpinion = async (blob) => {
  const { texto, origen } = await extraerTexto(blob);
  return { ...interpretarTexto(texto), origen };
};
